import GameEvents from "../const/GameEvents";
import MathUtil from "../utils/MathUtil";
import SoundVars from "../const/SoundVars";
import SoundController from "../utils/SoundController";
import Reel from "../views/Reel";
import App from "../App";
import { Ease } from "../Ease";
import { CustomEase } from "../CustomEase";
import spinModel from "../model/SpinModel";
import Layout from "../utils/Layout";
import Utils from "../utils/Utils";
import * as Easings from "../utils/Easings";

import ReelSymbol from "../views/Symbol";
import SpinModel from "../model/SpinModel";
import GameModel from "../model/GameModel";
import PromiseHelpers from "../utils/PromiseHelpers";
import { Tween } from "../Tween";
import { utils as PixiUtils } from "pixi.js";
import EventEmitter = PixiUtils.EventEmitter;
import { ConfigurationTypes } from '../const/ConfigurationTypes';
import ConfigManager from '../managers/ConfigManager';
import Configs from "../../../src/vendorConfigs/Configs";

/**
 * Animate reel symbols with mechanical spin animation.
 * PID stands for Proportional Integral Differential - derived from PID controllers. For more info :
 * https://en.wikipedia.org/wiki/PID_controller
 * To stop the reel a PID Controller approach is used where we compare the desired position to the current position
 * and apply control based on the difference.
 * The controlled variable is the reel's speed.
 */
export default abstract class AbstractReelBehaviour {
    /**
     * Use this to override the SpinModel.reelSetIndex.
     * Usefull, when you wish to have more than 1 reel group at the same time,
     * but they work on different reelSets. Such game is Haunted House.
     *
     * @type {(number | null)}
     * @memberof AbstractReelBehaviour
     */
    public reelSetIndex: number | null = null;

    /**
     * @constructor
     * @param reel {Reel} - the reel to be controlled
     */
    protected reel: Reel; // TODO interface for that
    protected isSpinning: boolean;
    protected hasAnticipation: boolean;
    protected currentPos: number;
    protected startStopPos: number;
    protected speed: number;
    protected maxSpeed: number;
    protected stopSymbols: number[];
    protected rollSnd: Howl;

    // protected unmaskThreshold: number;
    // protected disableUnmasking: boolean;
    protected stopPhase: boolean;
    protected stopSoundPlayed: boolean;
    protected timeScale: number;
    protected moveBackTween: Tween;
    protected speedUpTween: Tween | null;
    protected anticipationTween: Tween;

    protected currTime: number;
    protected direction: number;
    protected currStopTime: number;
    protected stopTime: number;
    protected _stopTime: number;
    protected _maxStopTime: number;
    protected maxStopTime: number;
    protected sortFunction: () => void;
    protected updateFunction: (time: number) => void;
    protected easingFunction: (p: number) => number;
    protected hitFunction: (
        symId: number,
        reelId: number,
        rowId: number
    ) => void;
    protected spinSound: string;
    protected stopSound: string | string[];
    protected looping: boolean;

    protected inFinalLoop: boolean = false;
    protected loopComplete: boolean = false;
    protected loopCounter = 0;
    protected swipeLimit = 0;
    protected swipeDelta = 0;
    protected inSwipeSpin = false;
    protected limitReached = false;
    protected inQuickStop = false;
    protected queueQuickStop = false;

    protected initialPos: number[] = [];
    protected _top: number = 0;
    protected _bottom: number = 0;
    protected started: boolean = false;

    constructor(
        reel: Reel,
        sortFunction: () => void,
        stopTime: number = App.config.stopTime,
        maxStopTime: number = App.config.maxStopTime
    ) {
        this.sortFunction = sortFunction;
        this.reel = reel;
        this.stopTime = stopTime || 0.8;
        this._stopTime = this.stopTime;
        this.maxStopTime = maxStopTime;
        this._maxStopTime = maxStopTime;
        this.isSpinning = false;
        this.hasAnticipation = false;
        this.currentPos = 0;
        this.speed = 0;
        this.loopCounter = 0;
        this.maxSpeed = App.config.maxReelSpeed;

        // TODO Check if this is needed when masking is handled elsewhere
        // this.unmaskThreshold =
        //     App.config.marginTop +
        //     this.reel.symStep *
        //         (this.reel.numSyms - this.reel.getExtraSymbolsCount()) +
        //     this.reel.yOrigin;
        // this.disableUnmasking = App.config.disableUnmasking;
        this.direction = 1;
        this.spinSound = SoundVars.EFFECT_REEL_SPIN;
        this.stopSound = [SoundVars.EFFECT_REEL_STOP];
        this.setInitialPositions();
        this.reel.spinner = this;
    }

    public updateSymbols() {

    }

    public updateFinalSymbols() {

    }

    public getDirection(): number {
        return this.direction;
    }

    public setSounds(sounds: { spin?: string; stop?: string | string[] }) {
        this.spinSound = sounds.spin || this.spinSound;
        let snd = sounds.stop || this.stopSound;
        this.stopSound = [];
        if (typeof sounds.stop === "string") {
            this.stopSound[0] = sounds.stop;
        } else {
            this.stopSound = snd;
        }
    }

    public flipDirection() {
        this.direction = -this.direction;
    }

    public activateAnticipation() {
        // TODO - when refactoring spinner the hasAnticipation property should initiate diferent stoping
        this.hasAnticipation = true;
        this.anticipationTween = Tween.to(this, 1, {
            speed: App.config.spinificationSpeed || this.maxSpeed / 2,
            ease: Ease.Power2InOut,
        });
    }

    public anticipationStarted() {
        return this.hasAnticipation;
    }

    public anticipationEnded() {
        return !this.isSpinning;
    }

    public isSettled() {
        return !this.isSpinning;
    }

    public isStarted() {
        return this.started;
    }

    public getStopAnimationDuration() {
        return this.stopTime;
    }

    public changeEase(
        stopTime: number,
        easingFunction = Easings.EaseInOutBack
    ) {
        this.stopTime = stopTime || 0.8;
        this.maxStopTime = stopTime * 2;
        this.easingFunction = easingFunction;
    }
    /**
     * Start the spin animation.
     * @public
     */
    public spin(easingFunction: any = CustomEase.parse(Ease.BackOut)) {
        this.started = true;
        if (App.config.reelSpinEase) {
            easingFunction = CustomEase.parse(App.config.reelSpinEase);
        }

        if (this.isSpinning === false) {
            this.isSpinning = true;
            this.hasAnticipation = false;
            this.rollSnd = SoundController.playSound(
                this.spinSound,
                true,
                undefined,
                App.config.splitSpinSound
            );

            if (this.rollSnd && App.config.reelRunFadeInTime) {
                const volume = Utils.isNumeric(App.config.soundVolumeOverride[this.spinSound])
                    ? App.config.soundVolumeOverride[this.spinSound]
                    : 1;

                SoundController.fadeSound(this.rollSnd, 0, volume, App.config.reelRunFadeInTime)
            }

            this.easingFunction = easingFunction;
            this.sortSymbols();
            this.stopPhase = false;
            this.inQuickStop = false;
            this.queueQuickStop = false;
            this.stopTime = this._stopTime;
            this.maxStopTime = this._maxStopTime;
            this.currentPos = 0;
            this.loopCounter = 0;
            this.loopComplete = false;
            this.stopSoundPlayed = false;
            this.looping = false;
            this.inFinalLoop = false;
            this.timeScale = 1;
            this._top = this.getTop();
            this._bottom = this.getBottom();
            this.setupSpeedupTweens();
            this.updateFunction = this.spinUpdate.bind(this);
            this.setupTimer();
            this.reel.resetSymbolsForSpin();

            App.subscribeToGameLoop(this.updateFunction);
        }
    }

    protected setupSpeedupTweens() {
        if (!this.inSwipeSpin) {
            this.moveBackTween = Tween.to(
                this,
                App.config.moveBackDuration || 0.08,
                {
                    speed: -this.maxSpeed / 30,
                    ease: Ease.Power2InOut,
                }
            );
        }
        this.speedUpTween = Tween.to(
            this,
            App.config.speedUpDuration || 0.3,
            {
                delay: this.inSwipeSpin
                    ? 0
                    : App.config.moveBackDuration || 0.08,
                speed: this.maxSpeed,
                ease: Ease.Power2In,
            }
        );
    }

    // Override for horizontal spinner
    protected setInitialPositions() {
        this.initialPos = [];
        this.reel.symbolsPositions.forEach((s, index) => {
            this.initialPos[index] = s.y;
        });
    }

    public setupTimer() {
        this.currTime = App.getActualTime();
    }

    public setupSwipe(limit: number) {
        this.setupTimer();
        this.timeScale = 1;
        this.speed = 1;
        this.limitReached = false;
        this.swipeLimit = this.symStep * limit;
        this.currentPos = 0;
        this._top = this.getTop();
        this._bottom = this.getBottom();
    }

    public updateSwipeSpeed(dSpeed: number) {
        this.swipeDelta = dSpeed;
    }

    // override for horizontal spinner
    public resetSwipe(duration): Promise<boolean> {
        let promises = [];

        let firstVizPos =
            this.initialPos[this.reel.getExtraSymbolsCount()] - this.symStep;
        let lastVizPos =
            this.initialPos[
            this.reel.symbols.length - this.reel.getExtraSymbolsCount() - 1
            ] + this.symStep;
        for (let index = 0; index < this.reel.symbols.length; index++) {
            let s = this.reel.symbols[index];
            if (s.y > firstVizPos && s.y < lastVizPos) {
                promises.push(
                    PromiseHelpers.tweenPromise(s, duration, {
                        y: this.initialPos[index],
                    })
                );
            } else {
                s.y = this.initialPos[index];
            }
        }
        return PromiseHelpers.combineParallel(promises);
    }

    // override for horizontal spinner
    public canSwipe(delta: number): boolean {
        if (!this.limitReached) {
            return true;
        }
        let yStep =
            Layout.scalePixels(this.speed) *
            delta *
            this.timeScale *
            this.direction;
        if (Math.abs(yStep + this.currentPos) < this.swipeLimit) {
            return true;
        }

        return false;
    }

    public setSwipeSpin() {
        this.inSwipeSpin = true;
    }

    public canSwipeSpin(fractionOfLimit: number): boolean {
        if (GameModel.gameConfig.swipeTimeLimit) {
            if (
                App.getActualTime() - this.currTime >
                GameModel.gameConfig.swipeTimeLimit
            ) {
                return false;
            }
        }
        if (
            this.currentPos * this.direction > 0 &&
            this.currentPos * this.direction >=
            this.swipeLimit * fractionOfLimit
        ) {
            return true;
        }

        return false;
    }

    /**
     * Stops the animation on the required final symbols.
     * @public
     * @param stopSymbolData {Array} - symbol ids of the symbols to stop on
     */
    public stop(
        stopSymbolData: number[],
        hasAnticipation = false,
        stopTime?: number
    ) {
        this.stopTime = stopTime || this.stopTime;
        this.maxStopTime = stopTime || this.maxStopTime;

        if (!this.stopPhase) {
            this.reel.emit(GameEvents.REEL_STOPPING, this.reel);
            this.stopPhase = true;
            SpinModel.applyStackReplacement(this.reel.reelIndex);

            if (this.moveBackTween) {
                this.moveBackTween.kill();
                this.moveBackTween = null;
            }

            if (this.anticipationTween) {
                this.anticipationTween.kill();
                this.anticipationTween = null;
            }
        }
    }

    /**
     * @public
     * Sets the parameters that control the reel animation so reel spins twice as fast and stops as fast as possible.
     * @param reelTurboTimeScale the time Scale can affect symbols changing during turbo spins, if you have jumping symbols during turbo spin override reelTurboTimeScale variable in ReeGroup it is undefined by default
    */
    public activateTurbo(reelTurboTimeScale) {
        this.queueQuickStop = true;
        if (reelTurboTimeScale) {
            this.timeScale = reelTurboTimeScale;
        } else {
            this.timeScale = 2;
        }
    }

    /**
     * @public
     * Sets the parameters that control the reel animation so reel speed returns to normal.
     */
    public deactivateTurbo() {
        this.queueQuickStop = false;
        this.timeScale = 1;
    }

    /**
     * @public
     * Sets the parameters that initiate fast stop.
     */
    public activateQuickStop() {
        this.queueQuickStop = true;
    }

    /**
     * @public
     * Sets the parameters that control the stopping animation so reel stops as fast as possible.
     */
    protected setupQuickStopVars() {
        if (!this.inQuickStop) {
            let stopTime = GameModel.gameConfig.quickStopTime || this.stopTime;

            // This makes the symbols jump, on manual stop!
            // let symStopCount = this.reel.numSyms + 1 * this.direction;

            // This one works correctly
            let symStopCount = this.reel.numSyms;
            let distance = this.getStopDistance(symStopCount);

            if (this.inFinalLoop && distance - this.currentPos < 1) {
                this.queueQuickStop = false;
                return;
            }
            this.inQuickStop = true;
            this.stopTime = stopTime;
            this.sortSymbols();
            SpinModel.resetCurrentStopsPerReel(this.reel.reelIndex, this.reelSetIndex);
            this.reel.unmaskSymbols();

            this.reel.symbols.forEach((s, index) => {
                const originalId = s.id;
                let id;
                if (this.direction > 0) {
                    id = spinModel.getNextStopPosition(
                        this.reel.reelIndex,
                        this.reelSetIndex,
                        0
                    );
                    this.setFakeId(s, id)
                    s.setId(id);
                } else {
                    id = spinModel.getNextStopPosition(
                        this.reel.reelIndex,
                        this.reelSetIndex,
                        this.reel.getExtraSymbolsCount() * 2 +
                        this.reel.numSyms
                    );
                    this.setFakeId(s, id)
                    s.setId(id);
                }

                // Ensure we won't change ids for visible symbols.
                const firstVisibleIndex = this.reel.getExtraSymbolsCount();
                const lastVisibleIndex = firstVisibleIndex + this.reel.numSyms - 1;
                if (index >= firstVisibleIndex && index <= lastVisibleIndex) {
                    this.setFakeId(s, originalId)
                    s.setId(originalId);
                }
            });

            SpinModel.resetCurrentStopsPerReel(this.reel.reelIndex, this.reelSetIndex);
            this.recalculateCurrentPositionForStop();

            if (this.speedUpTween) {
                this.speed = this.maxSpeed;
                this.speedUpTween.kill();
                this.speedUpTween = null;
            }
        }
    }

    /*
     * Update function for the spin animation
     * @private
     */
    spinUpdate(time: number) {
        if (!this.looping) {
            // Ensure we take into account the current FPS, otherwise the symbols will jump!
            const deltaSec = Math.abs(time - this.currTime) * Math.min(1, Math.max(1, App.instance.ticker.FPS) / 60);
            if (this.stopPhase) {
                if (this.queueQuickStop && !this.hasAnticipation) {
                    this.setupQuickStopVars();
                }
                this.spinStopUpdate(deltaSec);
            } else {
                this.spinLoopUpdate(deltaSec);
            }
        }
        this.currTime = time;
    }

    // override for horizontal spinner
    protected spinStopUpdate(time: number) {

        if (!this.isSettled()) {
            if (!this.inFinalLoop) {
                this.sortSymbols();
                this.inFinalLoop = true;
                this.currStopTime = 0;
                this.loopCounter = 0;
                this.recalculateCurrentPositionForStop();
                SpinModel.resetCurrentStopsPerReel(this.reel.reelIndex, this.reelSetIndex);
            }

            this.spinLoopUpdate(time);
        } else {
            this.sortSymbols();
            SpinModel.resetCurrentStopsPerReel(this.reel.reelIndex, this.reelSetIndex);
            this.reel.unmaskSymbols();

            this.reel.symbols.forEach((s, index) => {
                const id = spinModel.getNextStopPosition(
                    this.reel.reelIndex,
                    this.reelSetIndex,
                    this.reel.getExtraSymbolsCount() + 1
                );
                this.setFakeId(s, id)
                s.setId(id);
                s.y = this.initialPos[index];
                this.playHitAction(s.id, this.reel.reelIndex, index);
            });

            if (this.speedUpTween) {
                this.speedUpTween.kill();
                this.speedUpTween = null;
            }
            if (this.sortFunction) {
                this.sortFunction();
            }

            if (this.rollSnd) {
                if (this.timeScale > 1 || this.inQuickStop) {
                    SoundController.fadeStop(this.rollSnd, 0.1);
                } else {
                    SoundController.fadeStop(this.rollSnd, App.config.reelRunFadeOutTime || 0.5);
                }

                this.rollSnd = undefined;
            }

            this.inFinalLoop = false;
            this.stopPhase = false;
            this.inSwipeSpin = false;
            this.timeScale = 1;
            this.speed = 0;
            this.reel.emit(GameEvents.REEL_STOPPED, this.reel);
            this.started = false;
            App.unsubscribeToGameLoop(this.updateFunction);
        }
    }

    spinLoopUpdate(deltaSec: number) {
        this.looping = true;
        let dStep = 0;
        if (
            this.inSwipeSpin &&
            Layout.scalePixels(this.speed) * deltaSec <
            Layout.scalePixels(
                1 + (this.speed / this.maxSpeed) * this.timeScale
            )
        ) {
            dStep =
                Layout.scalePixels(1 + this.speed / this.maxSpeed) *
                this.direction *
                this.timeScale;
        } else {
            dStep =
                Layout.scalePixels(this.speed) *
                deltaSec *
                this.direction *
                this.timeScale;
        }

        let maxDistance = this._bottom - this._top;
        let symStopCount = this.inQuickStop
            ? this.direction > 0
                ? 1
                : 0
            : this.reel.numSyms + 1 * this.direction; //+1 for the slightly visible row below the reels
        let distance = this.getStopDistance(symStopCount);
        let distanceProgress = distance / maxDistance;
        let stopTime = Math.abs(distanceProgress * this.stopTime);
        let isLastUpdate = false;
        if (this.inFinalLoop) {
            this.currStopTime += deltaSec;
            let timeProgress = Math.min(
                1,
                (this.currStopTime / stopTime) * this.timeScale
            );
            let progress =
                this.timeScale > 1 || this.inQuickStop
                    ? timeProgress
                    : this.easingFunction(timeProgress);
            let targetPos = progress * distance;
            let targetStep =
                this.direction > 0
                    ? targetPos - this.currentPos
                    : targetPos - this.currentPos;
            let val = Math.min(Math.abs(targetStep), Math.abs(dStep));
            let sign = Math.sign(targetStep);
            dStep = val * sign;

            this.reel.symbols.forEach((s, index) => {
                s.setBlurLevel(1 - timeProgress);
            });

            isLastUpdate =
                (val <= 0.01 && this.currStopTime >= stopTime) ||
                this.currStopTime >= this.stopTime;
            this.playStopSound(stopTime);
        }

        // let looped = false;
        if (isLastUpdate) {
            this.sortSymbols();
        }

        dStep = dStep;
        this.reel.symbols.forEach((s, index) => {
            this.repositionSym(dStep, s, index, isLastUpdate, symStopCount);
        });

        this.currentPos += dStep;

        this.limitReached = Math.abs(this.currentPos) >= this.swipeLimit;
        this.loopComplete =
            this.loopCounter > 0 &&
            this.loopCounter % this.reel.symbols.length === 0;

        if (isLastUpdate) {
            this.isSpinning = false;
        }

        this.looping = false;
    }

    // override for horizontal spinner
    protected recalculateCurrentPositionForStop() {
        if (this.direction > 0) {
            this.currentPos = this.reel.symbols[0].y;
        } else {
            this.currentPos =
                this.reel.symbols[
                    this.reel.symbols.length - this.reel.getExtraSymbolsCount()
                ].y;
        }
    }

    // override for horizontal spinner
    protected repositionSym(
        dStep: number,
        s: ReelSymbol,
        index: number,
        isLastUpdate: boolean,
        symStopCount: number
    ) {
        let newPos = s.y + dStep;
        if (!this.inFinalLoop) {
            s.setBlurLevel(this.speed / this.maxSpeed);
        }
        s.visible = true;
        s.alpha = 1;

        if (isLastUpdate) {
            newPos = this.initialPos[index];
            s.setBlurLevel(0);
        }

        if (
            !isLastUpdate &&
            this.direction > 0 &&
            this.shouldWrapToBottom(newPos)
        ) {
            newPos = this._top + newPos - this._bottom;
            this.loopCounter++;
            const newId = spinModel.getPreviousStopPosition(
                this.reel.reelIndex,
                this.reelSetIndex,
                symStopCount
            )

            this.setFakeId(s, newId);
            s.setId(newId);
            // if(this.inFinalLoop && this.inQuickStop) {
            //     s.showDebugTag();
            // }
        }
        if (
            !isLastUpdate &&
            this.direction < 0 &&
            this.shouldWrapToTop(newPos)
        ) {
            newPos = this._bottom + newPos - this._top;
            const newId = spinModel.getNextStopPosition(
                this.reel.reelIndex,
                this.reelSetIndex,
                symStopCount
            );

            this.setFakeId(s, newId);
            s.setId(newId);

            this.loopCounter++;
            // if(this.inFinalLoop && this.inQuickStop) {
            //     s.showDebugTag();
            // }
        }

        s.y = newPos;
    }

    setFakeId(sym: ReelSymbol, id: number): void {
        if (!ConfigManager.hasConfig(ConfigurationTypes.SYMBOL_SPINE_ANIMATIONS)) {
            return;
        }

        const fakeConfig = Configs.symbolSpineAnimations.fakeSkinToIdMap;
        if (!fakeConfig) {
            return;
        }

        const config = fakeConfig.find(f => f.id === id);
        if (config) {
            sym.fakeId = config.pattern(sym);
        } else {
            sym.fakeId = null;
        }
    }

    shouldWrapToBottom(newPos: number) {
        return newPos > this._bottom;
    }

    shouldWrapToTop(newPos: number) {
        return newPos < this._top;
    }

    getTop(s?: ReelSymbol): number {
        // return this.reel.yOrigin;
        return this.initialPos[0] - this.symStep * 0.5;
    }

    getBottom(s?: ReelSymbol): number {
        // return this.reel.reelBottom;

        return this.initialPos[this.initialPos.length - 1] + this.symStep * 0.5;
    }

    get symStep(): number {
        return this.initialPos[1] - this.initialPos[0];
    }

    getStopDistance(numSyms: number): number {
        if (this.direction > 0) {
            // this is so this.reel.getOriginOffset()*2 because the origin is included in the _top property so it is removed once
            return (
                this.symStep *
                (numSyms - this.reel.getExtraSymbolsCount() + 0.5) -
                this._top +
                this.reel.getOriginOffset() * 2
            );
        } else {
            return this.symStep * (1 - numSyms + 0.5) + this._top;
        }
    }

    //Override to change sound play condition , function is called on each stopPhase spinLoopUpdate
    protected playStopSound(stopTime?) {
        const soundSettled =
            (stopTime || this.stopTime) - this.currStopTime <=
            (App.config.stopSoundOffset || 0.5);

        if (soundSettled && this.stopSoundPlayed === false) {
            this.stopSoundPlayed = true;

            if (typeof this.stopSound === "string") {
                SoundController.playSound(
                    this.stopSound,
                    false,
                    null,
                    !spinModel.stopImmediately && !GameModel.turboSpin
                );
            } else {
                this.stopSound.forEach((sndName) => {
                    SoundController.playSound(
                        sndName,
                        false,
                        null,
                        !spinModel.stopImmediately && !GameModel.turboSpin
                    );
                });
            }
        }
    }

    setCustomHit(
        hitFunction: (symId: number, reelId: number, rowId: number) => void
    ) {
        this.hitFunction = hitFunction;
    }

    protected playHitAction(symId: number, reelId: number, rowId: number) {
        if (
            rowId >= this.reel.getExtraSymbolsCount() &&
            rowId <=
            this.reel.symbols.length - this.reel.getExtraSymbolsCount() - 1
        ) {
            if (this.hitFunction) {
                this.hitFunction(symId, reelId, rowId);
            }
            if (App.config.hitSounds && App.config.hitSounds[symId]) {
                SoundController.playSound(App.config.hitSounds[symId]);
            }
        }
    }

    // override for horizontal spin
    protected sortSymbols() {
        this.reel.sortSymbolsByY();
    }

    public on(event: string, fn: EventEmitter.ListenerFn, context?: any) {
        this.reel.on(event, fn, context);
    }

    public off(event: string, fn: EventEmitter.ListenerFn, context?: any) {
        this.reel.off(event, fn, context);
    }

    public once(event: string, fn: EventEmitter.ListenerFn, context?: any) {
        this.reel.once(event, fn, context);
    }
}
